'use client';

import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { createClient } from '@/lib/supabase/client';

export interface ModulesEnabled {
  residential: boolean;
  channel: boolean;
  commercial: boolean;
}

const DEFAULT_MODULES: ModulesEnabled = {
  residential: true,
  channel: false,
  commercial: false,
};

interface ModulesContextValue {
  modules: ModulesEnabled;
  loading: boolean;
  updateModule: (key: keyof Omit<ModulesEnabled, 'residential'>, value: boolean) => Promise<void>;
  setModulesEnabled: (newModules: ModulesEnabled) => void;
  workspaceId: string | null;
}

const ModulesContext = createContext<ModulesContextValue>({
  modules: DEFAULT_MODULES,
  loading: true,
  updateModule: async () => {},
  setModulesEnabled: () => {},
  workspaceId: null,
});

export const useModules = () => useContext(ModulesContext);

function parseModules(raw: unknown): ModulesEnabled {
  const me = raw as ModulesEnabled | null;
  return {
    residential: me?.residential ?? true,
    channel: me?.channel ?? false,
    commercial: me?.commercial ?? false,
  };
}

export function ModulesProvider({ children }: { children: React.ReactNode }) {
  // Start with loading=true, modules=DEFAULT. Never initialize from server props.
  const [modules, setModules] = useState<ModulesEnabled>(DEFAULT_MODULES);
  const [loading, setLoading] = useState(true);
  const [workspaceId, setWorkspaceId] = useState<string | null>(null);

  // Refs always hold latest values — safe to read inside async callbacks
  const modulesRef = useRef<ModulesEnabled>(DEFAULT_MODULES);
  const workspaceIdRef = useRef<string | null>(null);

  // Single Supabase client created once at provider mount — never recreated
  const supabaseRef = useRef(createClient());

  // Keep refs in sync with state
  useEffect(() => { modulesRef.current = modules; }, [modules]);
  useEffect(() => { workspaceIdRef.current = workspaceId; }, [workspaceId]);

  // Client-side only fetch on mount — never runs on server, no hydration conflict
  useEffect(() => {
    const supabase = supabaseRef.current;

    async function init() {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          setLoading(false);
          return;
        }

        const { data: ws, error } = await supabase
          .from('workspaces')
          .select('id, modules_enabled')
          .eq('owner_user_id', user.id)
          .maybeSingle();

        if (error) {
          console.error('[ModulesContext] init SELECT error:', error);
          setLoading(false);
          return;
        }

        if (!ws) {
          setLoading(false);
          return;
        }

        const parsed = parseModules(ws.modules_enabled);
        modulesRef.current = parsed;
        setWorkspaceId(ws.id);
        workspaceIdRef.current = ws.id;
        setModules(parsed);
        console.log('[ModulesContext] init loaded:', parsed);
      } catch (err) {
        console.error('[ModulesContext] init error:', err);
      } finally {
        setLoading(false);
      }
    }

    init();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Direct state setter exposed for external use
  const setModulesEnabled = (newModules: ModulesEnabled) => {
    modulesRef.current = newModules;
    setModules(newModules);
  };

  /**
   * OPTIMISTIC UPDATE PATTERN:
   * 1. Read current state from ref (always fresh — no stale closure)
   * 2. Build new value
   * 3. SET STATE IMMEDIATELY (toggle flips before Supabase call)
   * 4. Call Supabase UPDATE
   * 5. On failure: revert state to previous and throw
   * 6. On success: state already correct — no re-fetch needed
   */
  const updateModule = async (
    key: keyof Omit<ModulesEnabled, 'residential'>,
    value: boolean
  ): Promise<void> => {
    const wsId = workspaceIdRef.current;

    if (!wsId) {
      const msg = '[ModulesContext] updateModule aborted: workspaceId is null';
      console.error(msg);
      throw new Error(msg);
    }

    const supabase = supabaseRef.current;

    // Step 1: snapshot current state from ref
    const previousModules = { ...modulesRef.current };

    // Step 2: build new value
    const newModules: ModulesEnabled = { ...previousModules, [key]: value };

    // Step 3: OPTIMISTIC — apply immediately so toggle flips at once
    modulesRef.current = newModules;
    setModules(newModules);
    console.log('MODULES UPDATE:', newModules);

    // Step 4: write to Supabase
    const { error } = await supabase
      .from('workspaces')
      .update({ modules_enabled: newModules })
      .eq('id', wsId);

    if (error) {
      console.error('[ModulesContext] Supabase UPDATE failed:', {
        message: error.message,
        code: error.code,
        details: error.details,
        hint: error.hint,
        workspaceId: wsId,
        key,
        value,
      });

      // Step 5: revert on failure
      modulesRef.current = previousModules;
      setModules(previousModules);
      console.log('MODULES REVERTED:', previousModules);
      throw error;
    }

    // Step 6: success — state is already correct, no re-fetch needed
    console.log('[ModulesContext] Supabase UPDATE success for key:', key, '=', value);
  };

  return (
    <ModulesContext.Provider value={{ modules, loading, updateModule, setModulesEnabled, workspaceId }}>
      {children}
    </ModulesContext.Provider>
  );
}
